import { Box, Typography, IconButton, Card } from "@mui/material";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { useResponsiveValues } from "../../../../common/Providers/DimentionsProvider/UseResponsiveValues";
import { useVehicleReservation } from "../../../../common/Providers/VehicleReservationProvider/UseVehicleReservation";
import { useState, useMemo } from "react";
import { useSnackbar } from "notistack";
import { API_URL } from '../../../../common/AppConstants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/free-regular-svg-icons';

interface props {
  product: any;
  sx?: object;
}

const ProductCard: React.FC<props> = ({ sx, product }) => {
  const { addVehicleReservationItems, removeVehicleReservationItem, VehicleReservationItems, VehicleReservationMain } = useVehicleReservation();
  const { enqueueSnackbar } = useSnackbar();

  const [imageLoadError, setImageLoadError] = useState(false);
  const [hovered, setHovered] = useState(false);
  const { matches900 } = useResponsiveValues();

  // Calculate quantity from VehicleReservationItems instead of local state
  const quantity = useMemo(() => {
    return VehicleReservationItems.filter((item: any) => item.family_id === product.id).length;
  }, [VehicleReservationItems, product.id]);

  const availableQuantity = useMemo(() => {
    if (product?.available_qty_at_locations?.length > 0) {
      return product.available_qty_at_locations.reduce((sum: number, loc: any) => sum + (loc.available_qty || 0), 0);
    }
    return VehicleReservationMain.availableSheet ? VehicleReservationMain.availableSheet[product.display_name] ?? 0 : Infinity;
  }, [product, VehicleReservationMain.availableSheet]);

  const isMaxCapacity = quantity >= availableQuantity;

  const handleQuantityIncrement = () => {
    if (quantity >= availableQuantity) {
      enqueueSnackbar("This product is out of stock.", {
        variant: 'error',
        style: { width: '350px' },
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return;
    }

    let priceGroupId = 0
    if (product?.lines[0]?.price_group_id){
      priceGroupId = product?.lines[0]?.price_group_id
    } else priceGroupId = product.priceGroupIds.split(',')[0]

    const newItem = {
      family_id: product.id,
      family: product.family,
      display_name: product.display_name,
      quantity: 1,
      price_group_id: priceGroupId,
      extras: [],
      img_url: product?.img_url ?? "",
    };

    addVehicleReservationItems([newItem]);
  }

  const handleQuantityDecrement = () => {
    if (quantity > 0) {
      const index = VehicleReservationItems.findIndex((item: any) => item.family_id === product.id);
      if (index !== -1) {
        removeVehicleReservationItem(index);
      }
    }
  }

  return (
    <Card
      key={product.id}
      sx={styles(matches900).productCard}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Box sx={styles(matches900).imageContainer}>
        {!imageLoadError ? (
          <>
            <img
              src={API_URL + product.img_url}
              alt={product.display_name}
              title={product.display_name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "transform 0.3s ease",
                transform: hovered ? "scale(1.05)" : "scale(1)",
              }}
              onError={() => setImageLoadError(true)}
              onLoad={() => setImageLoadError(false)}
            />
            <Box sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              background: hovered
                ? "rgba(0,0,0,0.55)"
                : "linear-gradient(transparent, rgba(0,0,0,0.35))",
              padding: "24px 12px 12px",
              transition: "background 0.3s ease",
            }}>
              <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#fff", textAlign: "center", textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>
                {product.display_name}
              </Typography>
            </Box>
          </>
        ) : (
          <Box sx={styles(matches900).imagePlaceholder}>
            <FontAwesomeIcon icon={faImage} style={{ width: "60px", height: "60px", color: "#ccc" }} />
            <Box sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              background: "linear-gradient(transparent, rgba(0,0,0,0.35))",
              padding: "24px 12px 12px",
            }}>
              <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#fff", textAlign: "center", textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>
                {product.display_name}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={styles(matches900).productDetails}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Box sx={styles(matches900).quantityControls}>
          <IconButton
            onClick={handleQuantityDecrement}
            disabled={quantity === 0}
            sx={{
              ...styles(matches900).quantityButton,
              "&:disabled": {
                backgroundColor: "#f5f5f5",
                color: "#ccc",
                border: "2px solid #e0e0e0",
              },
            }}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>

          <Box sx={{
              ...styles(matches900).quantityDisplay,
              ...(isMaxCapacity && { borderBottom: "2px solid #d32f2f" }),
            }}>
            <Typography sx={{
                ...styles(matches900).quantityText,
                ...(isMaxCapacity && { color: "#d32f2f" }),
              }}>
              {quantity}
            </Typography>
          </Box>

          <IconButton
            onClick={handleQuantityIncrement}
            disabled={isMaxCapacity}
            sx={{
              ...styles(matches900).quantityButton,
              "&:disabled": {
                backgroundColor: "#f5f5f5",
                color: "#ccc",
                border: "2px solid #e0e0e0",
              },
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>

      </Box>
      </Box>
    </Card>
  );
};

const styles = (matches900: boolean) => ({
  scheduleContainer: {
    width: "100%",
    padding: "16px",
    flex: 1,
    overflow: "auto",
  },
  scheduleHeader: {
    minHeight: "72px",
  },
  scheduleHeaderTitle: {
    color: "#333333",
    fontSize: "16px",
  },
  scheduleSubtitle: {
    color: "#333333",
    fontWeight: 700,
    fontSize: "16px",
    marginBottom: "8px",
  },
  productGrid: {
    display: "grid",
    gridTemplateColumns: matches900 ? "repeat(2, 1fr)" : "1fr",
    gap: "24px",
    marginBottom: "32px",
  },
  productCard: {
    borderRadius: "16px",
    overflow: "hidden",
    backgroundColor: "#ffffff",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    border: "1px solid #f0f0f0",
    transition: "box-shadow 0.2s ease",
    "&:hover": {
      boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
    },
  },
  imageContainer: {
    width: "100%",
    height: "220px",
    overflow: "hidden",
    backgroundColor: "#f8f8f8",
    position: "relative",
  },
  imagePlaceholder: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0f0f0",
  },
  productDetails: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    padding: "16px 20px 24px 20px",
  },
  productName: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#1a1a1a",
    textAlign: "center",
    lineHeight: 1.3,
  },
  quantityControls: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    width: "100%",
  },
  quantityButton: {
    width: "44px",
    height: "44px",
    borderRadius: "50%",
    border: "2px solid #2196F3",
    backgroundColor: "#ffffff",
    color: "#2196F3",
    "&:hover": {
      backgroundColor: "#2196F3",
      color: "#ffffff",
    },
  },
  quantityDisplay: {
    minWidth: "60px",
    textAlign: "center",
    borderBottom: "2px solid #2196F3",
    paddingBottom: "4px",
  },
  quantityText: {
    fontSize: "22px",
    fontWeight: 600,
    color: "#1a1a1a",
  },
  dateTimeSection: {
    marginTop: "24px",
  },
  timeSection: {
    marginTop: "32px",
    marginBottom: "32px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#333",
    marginBottom: "8px",
  },
  calendarContainer: {
    display: "grid",
    gridTemplateColumns: matches900 ? "1fr 1fr" : "1fr",
    gap: "24px",
    marginBottom: "16px",
  },
  monthCalendar: {
    backgroundColor: "#ffffff",
    borderRadius: "4px",
    padding: "16px",
    // border: "1px solid #e0e0e0",
  },
  monthHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "16px",
  },
  monthTitle: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#333",
  },
  weekDays: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "4px",
    marginBottom: "8px",
  },
  weekDay: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#666",
    textAlign: "center",
    padding: "8px 4px",
  },
  calendarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "2px",
  },
  calendarDay: {
    minWidth: "40px",
    height: "40px",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: 500,
    color: "#333",
    backgroundColor: "transparent",
    border: "none",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  availableDay: {
    backgroundColor: "#4CAF50 !important",
    color: "#ffffff !important",
    "&:hover": {
      backgroundColor: "#45a049 !important",
    },
  },
  selectedDay: {
    backgroundColor: "#2196F3 !important",
    color: "#ffffff !important",
    "&:hover": {
      backgroundColor: "#1976D2 !important",
    },
  },
  disabledDay: {
    color: "#ccc !important",
    backgroundColor: "transparent !important",
    "&:hover": {
      backgroundColor: "transparent !important",
    },
  },
  legend: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginTop: "16px",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  legendColor: {
    width: "16px",
    height: "16px",
    backgroundColor: "#4CAF50",
    borderRadius: "2px",
  },
  legendText: {
    fontSize: "14px",
    color: "#666",
  },
  timeSlots: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
  },
  timeSlot: {
    padding: "2px 4px",
    borderRadius: "4px",
    border: "2px solid #2196F3",
    backgroundColor: "#ffffff",
    color: "#2196F3",
    fontSize: "16px",
    fontWeight: 500,
    minWidth: "100px",
    "&:hover": {
      backgroundColor: "#f0f8ff",
    },
    "&:disabled": {
      borderColor: "#e0e0e0",
      color: "#ccc",
      backgroundColor: "#f5f5f5",
    },
  },
  selectedTimeSlot: {
    backgroundColor: "#2196F3 !important",
    color: "#ffffff !important",
    "&:hover": {
      backgroundColor: "#1976D2 !important",
    },
  },
  durationSection: {
    marginBottom: "32px",
  },
  durationDropdownContainer: {
    position: "relative",
    width: "100%",
    maxWidth: "600px",
  },
  durationDropdown: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    border: "2px solid #0047C0",
    borderRadius: "4px",
    backgroundColor: "#ffffff",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f8f9fa",
    },
  },
  durationDropdownText: {
    fontSize: "16px",
    color: "#333",
    fontWeight: 500,
  },
  durationDropdownMenu: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: "#ffffff",
    border: "2px solid #0047C0",
    borderTop: "none",
    borderRadius: "0 0 4px 4px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    zIndex: 1000,
    maxHeight: "300px",
    overflowY: "auto",
  },
  durationOption: {
    padding: "12px 16px",
    cursor: "pointer",
    borderBottom: "1px solid #e0e0e0",
    "&:hover": {
      backgroundColor: "#f0f8ff",
    },
    "&:last-child": {
      borderBottom: "none",
    },
  },
  selectedDurationOption: {
    backgroundColor: "#0047C0 !important",
    color: "#ffffff !important",
    "&:hover": {
      backgroundColor: "#003d9f !important",
    },
  },
  durationOptionText: {
    fontSize: "16px",
    fontWeight: 400,
  },
});

export default ProductCard;
